import { QueryObserverResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  bulkUpsertSpending,
  getAllocationOfCostsChartData,
  getCostByInitiativeChartData,
  getCostVsOutputChartData,
  getSpending,
} from '../../api/financials-client/financials-client';
import type {
  AllocationOfCostsChartResponseByCategory,
  CostByInitiativeResponse,
  CostVsBudgetChartResponse,
  Spending,
  SpendingDetail,
} from '../../api/financials-client/financials-client.type';
import { getProjectsByPortfolioId } from '../../api/portfolio-client/portfolio-client';
import type { Project } from '../../api/projects-client/projects-client.type';

/**
 * Get the spending data
 * @param portfolioId - The portfolio ID
 * @returns The spending data
 */
function useSpending(portfolioId: string | undefined): {
  data: Spending[] | undefined;
  query: QueryObserverResult<Spending[]>;
} {
  const query = useQuery({
    queryKey: ['getSpending', portfolioId],
    queryFn: async () => {
      return portfolioId ? getSpending(portfolioId) : Promise.reject('Missing Portfolio ID');
    },
    enabled: Boolean(portfolioId),
  });

  return { data: (query.data || []).filter((entry: any) => entry.subproject_id !== null), query };
}

/**
 * Use the save spending mutation
 * @param payload - The payload to save the spending
 * @returns The mutation result
 */
function useSaveSpendingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      portfolio_id: string;
      year: number;
      projects: Record<string, Record<string, SpendingDetail[]>>;
    }) => {
      return bulkUpsertSpending(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getSpending'] });
    },
  });
}

/**
 * Get the cost by initiative chart data
 * @param startDate - The start date
 * @param endDate - The end date
 * @param portfolioId - The portfolio ID
 * @param teamId - The team ID
 * @param boardId - The board ID
 */
function useCostByInitiative(
  startDate: Date | null,
  endDate: Date | null,
  portfolioId: string | null,
  teamId: string | null,
  boardId: string | null,
): { data: CostByInitiativeResponse[] | undefined; query: QueryObserverResult<CostByInitiativeResponse[]> } {
  const query = useQuery<CostByInitiativeResponse[]>({
    queryKey: ['getCostByInitiativeData', startDate, endDate, portfolioId, teamId, boardId] as const,
    queryFn: async () => {
      if (!portfolioId || !startDate || !endDate) {
        throw new Error('Missing required parameters');
      }
      return await getCostByInitiativeChartData(
        portfolioId,
        startDate,
        endDate,
        teamId || undefined,
        boardId || undefined,
      );
    },
    enabled: !!(portfolioId && startDate && endDate),
  });

  return { data: query.data, query };
}

/**
 * Get the cost versus output chart data
 * @param startDate - The start date
 * @param endDate - The end date
 * @param portfolioId - The portfolio ID
 * @param teamId - The team ID
 * @param boardId - The board ID
 * @returns The cost versus output chart data
 */
function useCostVersusOutput(
  startDate: Date | null,
  endDate: Date | null,
  portfolioId: string | null,
  teamId: string | null,
  boardId: string | null,
): { data: CostVsBudgetChartResponse | undefined; query: QueryObserverResult<CostVsBudgetChartResponse> } {
  const query = useQuery<CostVsBudgetChartResponse>({
    queryKey: ['getCostVsOutputChartData', startDate, endDate, portfolioId, teamId, boardId],
    queryFn: async () => {
      if (!portfolioId || !startDate || !endDate) {
        throw new Error('Missing required parameters');
      }

      return getCostVsOutputChartData(startDate, endDate, portfolioId, teamId || undefined, boardId || undefined);
    },
    enabled: !!(portfolioId && startDate && endDate),
  });

  return { data: query.data, query };
}

/**
 * Get the allocation of costs chart data
 * @param startDate - The start date
 * @param endDate - The end date
 * @param portfolioId - The portfolio ID
 * @param teamId - The team ID
 * @param subprojectId - The subproject ID
 * @returns The allocation of costs chart data
 */
function useAllocationOfCosts(
  startDate: Date | null,
  endDate: Date | null,
  portfolioId: string | null,
  teamId: string | null,
  subprojectId: string | null,
): {
  data: AllocationOfCostsChartResponseByCategory | undefined;
  query: QueryObserverResult<AllocationOfCostsChartResponseByCategory>;
} {
  const query = useQuery<AllocationOfCostsChartResponseByCategory>({
    queryKey: ['getAllocationOfCosts', startDate, endDate, portfolioId, teamId, subprojectId],
    queryFn: async () => {
      if (!portfolioId || !startDate || !endDate) {
        throw new Error('Missing required parameters');
      }
      return getAllocationOfCostsChartData(portfolioId, startDate, endDate, teamId, subprojectId);
    },
    enabled: !!(portfolioId && startDate && endDate),
  });

  return { data: query.data, query };
}

/**
 * Get projects and boards for a portfolio
 * @param portfolioId - The portfolio ID
 * @returns The portfolio projects and boards
 */
function usePortfolioProjectsAndBoards(portfolioId: string | undefined): {
  data: Project[] | undefined;
  query: QueryObserverResult<Project[]>;
} {
  const query = useQuery({
    queryKey: ['getPortfolioProjectsAndBoards', portfolioId],
    queryFn: async () => {
      return portfolioId
        ? getProjectsByPortfolioId(portfolioId, ['id', 'name', 'subprojects'])
        : Promise.reject('Missing Portfolio ID');
    },
    enabled: Boolean(portfolioId),
  });
  const filteredAndSorted = useMemo(() => {
    const projects = query.data ?? [];
    return projects.filter((project) => project.subprojects.length > 0).sort((a, b) => a.name.localeCompare(b.name));
  }, [query.data]);

  return { data: filteredAndSorted, query };
}

export {
  useAllocationOfCosts,
  useCostByInitiative,
  useCostVersusOutput,
  usePortfolioProjectsAndBoards,
  useSaveSpendingMutation,
  useSpending,
};
